'use client';

import { Box, Modal, ScrollArea } from '@mantine/core';
import { modalState } from '@/features/privacyPolicy/privacyPolicySlice';
import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks';
import { StandardButton } from '@/shared/StandardButton';
import { useFocusTrap, useMediaQuery } from '@mantine/hooks';

export const PrivacyPolicyModal = () => {
  const isOnLargeScreen = useMediaQuery('(min-width: 1060px)');
  const focusTrapRef = useFocusTrap();
  const privacyPolicyModalOpen = useAppSelector(
    (state) => state.privacyPolicyModal.privacyPolicyModalOpen
  );
  const dispatch = useAppDispatch();

  const handleClick = () => {
    dispatch(modalState(false));
  };

  return (
    <Modal
      opened={privacyPolicyModalOpen}
      onClose={() => {}}
      withCloseButton={false}
      centered
      size={isOnLargeScreen ? '50%' : '95%'}
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 5,
      }}
      style={{}}
      classNames={{
        content: 'normal-shape-light-background',
      }}
      styles={{
        content: {
          borderRadius: '16px',
          zIndex: 1000,
        },
        overlay: {
          zIndex: 999,
        },
        inner: {
          zIndex: 1000,
        },
      }}
    >
      <Box className="h-full w-full p-5 text-lightBlue-900" ref={focusTrapRef}>
        <h1 className="text-xl font-bold">PRIVACY POLICY</h1>
        <ScrollArea
          h={`50vh`}
          scrollbarSize={6}
          className="mb-8 mt-4 pr-4"
          styles={{
            thumb: {
              color: '#ffffff',
            },
            scrollbar: {
              color: '#ffffff',
            },
          }}
        >
          <p className="mt-4">
            <h2 className="mt-4 font-bold">PRIVACY POLICY</h2>
            {`Your privacy is very important for us. This Privacy Policy is meant to inform you about our practices regarding collection, use, and disclosure of information that you may provide to this website. Please be sure to read all of this Privacy Policy before using or submitting information to this website. Arcanis Research LTD does not disclose, rent, or sell any personal information about site users to any third parties. More detailed information on the information we may collect from you and how we use it is contained in the sections below.`}
          </p>

          <h2 className="mt-4 font-bold">DEFINITIONS</h2>
          <p className="mt-4">
            {`For the purposes of this Privacy Policy:
            "Personal Data" means any information relating to an identified or identifiable natural person.
            "Processing" means any operation performed on Personal Data.
            "Data Subject" means any identified or identifiable natural person whose Personal Data is being processed.
            "Controller" means the entity that determines the purposes and means of the processing of Personal Data.
            "Processor" means an entity that processes Personal Data on behalf of the Controller.
            "Site" refers to our website.
            "Service" refers to the services we provide.
`}
          </p>

          <h2 className="mt-4 font-bold">THE CONTROLLER</h2>
          <p className="mt-4">
            {`We jointly administer, within the meaning of Article 26 of the regulation (EU) 2016/679 of the European Parliament and of the Council of 27 April 2016 on the protection of natural persons with regard to the processing of personal data and on the free movement of such data and repealing Directive 95/46/EC (General Data Protection Regulation) (“GDPR”), your personal data within the Arcanis Research LTD.
The controller of your personal data is: ARCANIS RESEARCH LTD, a company incorporated and existing under the laws of the Republic of Cyprus, registration number HE448551, with its registered office at Cheilonos, 2A, THE RIVERSIDE FORUM, 2nd floor, 1101, Nicosia, Cyprus.
You may find our joint arrangements in this Privacy Policy.
`}
          </p>

          <h2 className="mt-4 font-bold">
            YOUR DATA PROTECTION RIGHTS UNDER GENERAL DATA PROTECTION REGULATION
            (GDPR)
          </h2>
          <p className="mt-4">
            {`If you are a resident of the European Economic Area (EEA), you have certain data protection rights. Arcanis Research LTD aims to take reasonable steps to allow you to correct, amend, delete, or limit the use of your personal data.
We process your personal data, including: e-mail address, IP addresses and other information collected passively (“Personal Data”).
You may have the following rights:
1.	Right to access, update, or delete You can access, update, or request deletion of your personal data. Please contact us for assistance.
2.	Right of rectification: You have the right to correct inaccurate or incomplete information.
3.	Right to object: You may object to our processing of your personal data.
4.	Right of restriction: You can request that we restrict the processing of your personal information.
5.	Right to data portability: You have the right to receive a copy of your information in a structured, machine-readable, and commonly used format.
6.	Right to withdraw consent: You may withdraw your consent at any time where Arcanis Research LTD relied on your consent to process your personal information.
Requests regarding the exercise of any of the rights above or any complaints should be sent to info@arcaniscapital.com  using the registered email address you disclosed to us. We may ask you to verify your identity before responding to such requests.
We aim to respond to all requests within one month. For complex inquiries or when you've submitted multiple requests, our response may take longer. If this happens, we'll let you know within one month of receiving your request and provide regular updates on our progress.
If you're unhappy with how we've addressed your complaint, you can escalate it to the appropriate supervisory authority which is the Cyprus Data Protection Commissioner. You also have the option to file a complaint with the data protection authority in your country of residence.
Details regarding the Cyprus Data Protection Commissioner can be found here:
https://www.dataprotection.gov.cy 
You have the right to complain to a Data Protection Authority about our collection and use of your Personal Data. For more information, please contact your local data protection authority in the European Economic Area (EEA).

`}
          </p>

          <h2 className="mt-4 font-bold">INFORMATION COLLECTION</h2>

          <p className="mt-2">
            {`Arcanis Research LTD is the sole owner of any information collected on this site. We do not sell, share, or rent this information to others. We process personal information lawfully, fairly, and transparently for specified, explicit, and legitimate purposes. The type of information we collect is set out on our Site and in any verbal or written questions, application forms or other information collection forms or templates that we provide. This Privacy Policy also applies to other personal information that you choose to provide.
Use of our Site
We may log information about your access and use of our Site, including:
-	Internet cookies
-	Communications with our Site
-	Browser type
-	Operating system
-	Internet service provider domain name
This information is typically general and does not identify you personally.
Cookies and Similar Technologies
We use cookies, web beacons, pixel tags, and similar technologies to:
-	Remember your preferences
-	Understand how you use our Site
-	Improve your user experience
-	Personalize content and advertisements
-	Analyze Site traffic and performance
You can manage cookie preferences through your browser settings. Refusing or deleting cookies may impact your experience on our Site.
Opinions and feedback
We may invite you to respond to questionnaires, surveys, or market research. Providing this information is optional.
`}
          </p>

          <h2 className="mt-4 font-bold">USE OF PERSONAL INFORMATION</h2>
          <p className="mt-2">
            {`We use your personal information for purposes including: to contact and communicate with you – on the basis of our legitimate interest (Article 6 sec. 1 lit. f GDPR); to consider your request for the service – on the basis of our legitimate interest (Article 6 sec. 1 lit. f GDPR; to enable us to provide the service to you – on the basis of Article 6 sec. 1 lit. b og GDPR ; to inform you about other services that may be of interest to you- on the basis of our legitimate interest (Article 6 sec. 1 lit. f GDPR);`}
          </p>

          <h2 className="mt-4 font-bold">DISCLOSURE OF YOUR INFORMATION</h2>
          <p className="mt-2">
            {`We may disclose your personal information to:
Service Providers
Third-party vendors, consultants, and other service providers who perform services on our behalf, such as payment processing, data analysis, email delivery, hosting services, customer service, and marketing assistance.
Business Transfers
In connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business by another company.
Legal Requirements
If required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).
Protection of Rights
To protect the rights, property, or safety of Arcanis Research LTD, our customers, or others.
With Your Consent
In other circumstances where we have your consent or are otherwise legally permitted to do so.
`}
          </p>
          <h2 className="mt-4 font-bold">STORAGE AND SECURITY</h2>
          <p className="mt-2">
            {`We are committed to ensuring that the information you provide is secure. In order to prevent unauthorized access or disclosure, we have put in place suitable physical, electronic and managerial procedures to safeguard and secure the information and protect it from misuse, interference, loss and unauthorized access, modification and disclosure.`}
          </p>
          <h2 className="mt-4 font-bold">DATA RETENTION</h2>
          <p className="mt-2">
            {`We will retain your personal data only for as long as necessary to fulfill the purposes for which we collected it, including for the purposes of satisfying any legal, regulatory, tax, accounting, or reporting requirements. We store your personal data until the expiration of the period of limitation of claims at the latest, unless longer retention is required by law.
To determine the appropriate retention period, we consider:
•	The amount, nature, and sensitivity of the personal data
•	The potential risk of harm from unauthorized use or disclosure
•	The purposes for which we process the data
•	Whether we can achieve those purposes through other means
•	Legal, regulatory, and contractual requirements
`}
          </p>
          <h2 className="mt-4 font-bold">NEWS AND INFORMATION</h2>
          <p className="mt-2">
            {`Arcanis Research LTD plans to publish news on the site as well as send information via email if you wish to subscribe to the Newsletter. We respect your privacy and provide options to opt out of these communications. You can opt out of receiving marketing communications from us at any time by:
Following the unsubscribe instructions included in each marketing email
Contacting us using the contact information provided below
Please note that even if you opt out of marketing communications, we may still send you service-related communications.
`}
          </p>
          <h2 className="mt-4 font-bold">LOG FILES</h2>
          <p className="mt-2">
            {`We use Internet Protocol (IP) addresses to analyze trends, administer the site, track user’s movement, and gather broad demographic information for aggregate use. As an example, we may analyze the total number of users of our site in a given time period, or the general nature of the questions that are emailed to us. IP addresses are not linked to personally identifiable information.`}
          </p>
          <h2 className="mt-4 font-bold">NOTIFICATION OF CHANGES</h2>
          <p className="mt-2">
            {`If we change our privacy policy, we will post those changes on this Privacy Policy section so our users are always aware of what information we collect, how we use it, and under what circumstances, if any, we disclose it. If at any point we decide to use personally identifiable information in a manner different from that stated at the time it was collected, we will notify users immediately by way of email. Users will have a choice as to whether or not we may use their information in this different manner. We will only use information in accordance with the privacy policy under which the information was collected. If you wish to be informed what Personal Data we hold about you or/and if you want it to be removed from our systems, please contact us: info@arcaniscapital.com.`}
          </p>
          <h2 className="mt-4 font-bold">
            TRANSFERS OF PERSONAL DATA TO THIRD COUNTRIES{' '}
          </h2>
          <p className="mt-2">
            {`We store your data in IT infrastructure provided by third parties, including email inboxes whose servers may be located outside the European Economic Area, and thus transfer personal data to recipients located outside the European Economic Area. In any case we transfer the Personal Data using mechanisms that comply with applicable law, in particular in accordance with the requirements of the GDPR, contained, inter alia, in Article 46 of the GDPR, in particular on the basis of standard contractual clauses. For more information about the existing safeguards implemented by the us to ensure the processing of Personal Data in accordance with applicable laws, and about the possibility of obtaining a copy of the data or about where and how to make the data available, please contact us as indicated in this clause
`}
          </p>
          <h2 className="mt-4 font-bold">
            INFORMATION ON AUTOMATED DECISION-MAKING
          </h2>
          <p className="mt-2">
            {`We do not make decisions based on automated processing, including profiling based on your personal data.`}
          </p>
          <h2 className="mt-4 font-bold">JOINT CONTROLLERS ARRANGEMENTS</h2>
          <p className="mt-2">
            {`Please see below the most important provisions arising from the agreement between the joint controllers:
1.	Arcanis Research Ltd is the leading Joint Controller
2.	Each Joint Controller shall obtain consent for processing personal data when required and provide information in accordance with Articles 13 or 14 of the GDPR
3.	The Joint Controller who receives a request from a data subject is responsible for responding unless only the leading Joint Controller can comply
4.	Joint Controllers cooperate to respond to data subject requests and jointly decide on data deletion requests
5.	In case of a personal data breach, the Joint Controller from whose act or omission the breach arose is responsible for management and notifications
6.	For cross-border processing, notifications of personal data breaches are submitted to the Lead Supervisory Authority (Commissioner for Personal Data Protection Cyprus) and other relevant supervisory authorities
7.	Joint Controllers apply appropriate technical and organizational measures to ensure data security in accordance with Article 32 of GDPR
8.	Processors may be used under written agreements, with other Joint Controllers having the right to object for legitimate reasons
9. Joint Controllers are jointly and severally liable for damages caused to data subjects as a result of GDPR breaches
`}
          </p>
          <h2 className="mt-4 font-bold">CONTACT INFORMATION</h2>
          <p className="mt-2">
            {`In matters concerning the processing of personal data, you may contact us: by directing traditional correspondence to our registered address, or via a dedicated e-mail address: info@arcaniscapital.com.
`}
          </p>
          <h2 className="mt-4 font-bold">DATA PROTECTION OFFICER</h2>
          <p className="mt-2">
            {`We have appointed a Data Protection Officer (DPO) responsible for overseeing questions concerning this Privacy Policy. If you have any questions about this Privacy Policy, including any requests to exercise your legal rights, please contact our DPO at info@arcaniscapital.com.
`}
          </p>
        </ScrollArea>
        <Box className="flex items-center xxs:max-md:flex-col xxs:max-md:gap-6 md:gap-12">
          <StandardButton onClick={handleClick} disabled={false}>
            I understand
          </StandardButton>
        </Box>
      </Box>
    </Modal>
  );
};
